import { APP_CONFIG } from 'mangools-commons/dist/configs/app'
import queryString from 'query-string';
import { parse, parseResetData } from 'sources/parsers/ResultDataParser';

import ErrorCodes from 'mangools-commons/dist/constants/ErrorCodes';
import HumanService from 'mangools-commons/dist/services/HumanService';
import { TechnicalErrorReportService } from 'mangools-commons/dist/services/TechnicalErrorReportService';

// Constants
const ENDPOINT = '/serps';
const RESET_ENDPOINT = '/serps/reset';

/**
 * Data source class handling communication
 * with API for serp result list data.
 */
class ResultSource {
    static getData({ accessToken, disableCache, params, page }) {
        let query = null;

        if (disableCache === true) {
            query = queryString.stringify({
                cacheTime: 0,
                kw: params.keyword,
                location_id: params.locationId,
                page,
                parserType: 1,
                platform_id: params.platformId,
            });
        } else {
            query = queryString.stringify({
                kw: params.keyword,
                location_id: params.locationId,
                page,
                parserType: 1,
                platform_id: params.platformId,
            });
        }

        const requestURL = `${APP_CONFIG.API_HOST}${APP_CONFIG.API_PATH}${ENDPOINT}?${query}`;

        return fetch(requestURL, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'X-Access-Token': accessToken || '',
                'X-Human-Token': HumanService.gen(),
                'X-Mangools-Client': 'app',
            },
        })
            .then(response => {
                if (response.ok) {
                    if (response.status === 204) {
                        return {
                            error: false,
                            payload: null,
                        };
                    } else {
                        return response
                            .json()
                            .then(data => ({
                                error: false,
                                payload: parse(data),
                            }))
                            .catch(ex => {
                                const payload = {
                                    requestURL,
                                    status: ErrorCodes.PARSE_ERROR,
                                    text: ex.toString(),
                                };

                                TechnicalErrorReportService.report({ ...payload, method: 'GET' });

                                return { error: true, payload };
                            });
                    }
                } else {
                    return response.json().then(e => {
                        const payload = {
                            requestURL,
                            status: response.status,
                            text: e.error ? e.error.message : null,
                            type: e.error ? e.error.type : null,
                            retryAfter: e.error ? e.error.retry_after : null,
                        };

                        TechnicalErrorReportService.report({ ...payload, method: 'GET' });

                        return { error: true, payload };
                    });
                }
            })
            .catch(ex => {
                const payload = {
                    requestURL,
                    status: ErrorCodes.FETCH_ERROR,
                    text: ex.toString(),
                };

                TechnicalErrorReportService.report({ ...payload, method: 'GET' });

                return { error: true, payload };
            });
    }

    static getResetData({ accessToken, disableCache, params, page, fetchedSerpsCount }) {
        let query = null;

        if (disableCache === true) {
            query = queryString.stringify({
                kw: params.keyword,
                location_id: params.locationId,
                page,
                parserType: 1,
                platform_id: params.platformId,
                serp_count: fetchedSerpsCount || 1,
            });
        } else {
            query = queryString.stringify({
                kw: params.keyword,
                location_id: params.locationId,
                page,
                parserType: 1,
                platform_id: params.platformId,
                serp_count: fetchedSerpsCount || 1,
            });
        }

        const requestURL = `${APP_CONFIG.API_HOST}${APP_CONFIG.API_PATH}${RESET_ENDPOINT}?${query}`;

        return fetch(requestURL, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'X-Access-Token': accessToken || '',
                'X-Human-Token': HumanService.gen(),
                'X-Mangools-Client': 'app',
            },
        })
            .then(response => {
                if (response.ok) {
                    if (response.status === 204) {
                        return {
                            error: false,
                            payload: null,
                        };
                    } else {
                        return response
                            .json()
                            .then(data => ({
                                error: false,
                                payload: parseResetData(data),
                            }))
                            .catch(ex => {
                                const payload = {
                                    requestURL,
                                    status: ErrorCodes.PARSE_ERROR,
                                    text: ex.toString(),
                                };

                                TechnicalErrorReportService.report({ ...payload, method: 'GET' });

                                return { error: true, payload };
                            });
                    }
                } else {
                    return response.json().then(e => {
                        const payload = {
                            requestURL,
                            status: response.status,
                            text: e.error ? e.error.message : null,
                            type: e.error ? e.error.type : null,
                            retryAfter: e.error ? e.error.retry_after : null,
                        };

                        TechnicalErrorReportService.report({ ...payload, method: 'GET' });

                        return { error: true, payload };
                    });
                }
            })
            .catch(ex => {
                const payload = {
                    requestURL,
                    status: ErrorCodes.FETCH_ERROR,
                    text: ex.toString(),
                };

                TechnicalErrorReportService.report({ ...payload, method: 'GET' });

                return { error: true, payload };
            });
    }

    static getSnapshot({ accessToken, serpSnapshotId }) {
        const requestURL = `${APP_CONFIG.API_HOST}${APP_CONFIG.API_PATH}${ENDPOINT}/${serpSnapshotId}/snapshot`;

        return fetch(requestURL, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'X-Access-Token': accessToken || '',
                'X-Human-Token': HumanService.gen(),
                'X-Mangools-Client': 'app',
            },
        })
            .then(response => {
                if (response.ok) {
                    return response
                        .json()
                        .then(data => ({
                            error: false,
                            payload: data,
                        }))
                        .catch(ex => {
                            const payload = {
                                requestURL,
                                status: ErrorCodes.PARSE_ERROR,
                                text: ex.toString(),
                            };

                            TechnicalErrorReportService.report({ ...payload, method: 'GET' });

                            // No `error: true` here, unlike every sibling branch. Left as it was.
                            return { payload };
                        });
                } else {
                    return response.json().then(e => {
                        const payload = {
                            requestURL,
                            status: response.status,
                            text: e.error ? e.error.message : null,
                            type: e.error ? e.error.type : null,
                            retryAfter: e.error ? e.error.retry_after : null,
                        };

                        TechnicalErrorReportService.report({ ...payload, method: 'GET' });

                        return { error: true, payload };
                    });
                }
            })
            .catch(ex => {
                // This payload carries the code under `error`, not `status` like every sibling
                // branch. Left as it was, so the reporter gets the status spelled out instead.
                const payload = {
                    requestURL,
                    error: ErrorCodes.FETCH_ERROR,
                    text: ex.toString(),
                };

                TechnicalErrorReportService.report({
                    requestURL,
                    method: 'GET',
                    status: ErrorCodes.FETCH_ERROR,
                    text: payload.text,
                });

                return { error: true, payload };
            });
    }
}

export default ResultSource;
